import { MissingRefreshTokenError, type Auth0Client } from "@auth0/auth0-spa-js";

import tokenManager from "@/utils/tokenManager";
import { getClient } from "./auth0Client";
import { clearLocalSession } from "./clearLocalSession";
import { isAuth0Enabled } from "./authConfig";
import {
  getAuthStatus,
  isNavigationLatched,
  isTerminal,
  setAuthStatus,
} from "./authState";

/**
 * Errors that mean "this user must interact with Auth0 again". Anything else
 * (network, timeout, config) is a real failure and must not silently redirect.
 *
 * `invalid_grant` is the one the token endpoint returns when the rotating
 * refresh token has expired, been revoked, or was already redeemed ("Unknown or
 * invalid refresh token") -- the ordinary outcome of leaving the app shut for
 * longer than the refresh-token lifetime. spa-js rethrows it untouched, since
 * the iframe fallback that would otherwise absorb it is off, so leaving it out
 * here let it escape the ky `beforeRequest` hook: no redirect, every request
 * rejected, the page wedged until the user cleared site data by hand.
 */
const LOGIN_REQUIRED_CODES = new Set([
  "login_required",
  "consent_required",
  "interaction_required",
  "mfa_required",
  "invalid_grant",
]);

export const isLoginRequiredError = (error: unknown): boolean => {
  if (error instanceof MissingRefreshTokenError) return true;
  const code = (error as { error?: string } | null)?.error;
  return typeof code === "string" && LOGIN_REQUIRED_CODES.has(code);
};

/** Strips the auth params so a replayed `returnTo` cannot re-enter the callback. */
export const currentReturnTo = (): string => {
  const { pathname, search, hash } = window.location;
  const params = new URLSearchParams(search);
  for (const key of ["code", "state", "error", "error_description"]) {
    params.delete(key);
  }
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ""}${hash}`;
};

/**
 * Drops the local Auth0 state before handing off to the login redirect.
 *
 * spa-js does not evict the cache entry when the token endpoint rejects the
 * refresh token, and the separate `@@user@@` entry behind `getUser()` carries no
 * expiry at all -- so a dead session keeps reporting `isAuthenticated: true` to
 * auth0-react (and so to <Auth0Gate> and the `authenticated` phase) while every
 * token acquisition fails. Clearing both is what makes the recovery stick
 * instead of landing back in the same state on the next load.
 *
 * `openUrl: false` keeps this local: no navigation of its own, and the Auth0
 * session cookie is left alone, so the `loginWithRedirect` that follows can
 * still complete without a prompt when the session there is still good.
 *
 * The app's own session keys go with it, via the same teardown an explicit
 * logout runs: an expired session is still an ended session, so leaving the
 * assistant conversation and client ids behind would hand them to whoever logs
 * in next on this machine.
 */
const discardLocalSession = async (client: Auth0Client): Promise<void> => {
  try {
    await client.logout({ openUrl: false });
  } catch (error) {
    console.warn("[Auth] Could not clear the stale Auth0 session", error);
  }
  // Cleanup is best-effort: `requestLogin`'s catch is terminal, so a throw here
  // would skip `loginWithRedirect` and strand the user on the blocking screen.
  try {
    await clearLocalSession();
  } catch (error) {
    console.warn("[Auth] Could not clear the local session", error);
  }
};

/**
 * Sends the user to Auth0. Latches `redirecting` rather than clearing a guard
 * in a `finally`: `loginWithRedirect` resolves once navigation has been
 * *initiated*, and the document stays alive for some time after that, so a
 * finally-cleared guard would admit a second redirect before unload.
 *
 * Never redirects out of a terminal or already-navigating state -- a blocked
 * principal cannot be fixed by logging in again, and a logout in flight must
 * not be turned back into a login.
 */
export const requestLogin = async (returnTo?: string): Promise<void> => {
  if (isNavigationLatched() || isTerminal()) return;

  setAuthStatus({ phase: "redirecting" });
  try {
    const client = await getClient();
    await discardLocalSession(client);
    await client.loginWithRedirect({
      appState: { returnTo: returnTo ?? currentReturnTo() },
    });
  } catch (error) {
    setAuthStatus({
      phase: "failed",
      reason: "CONFIG_INVALID",
      detail: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

const bearer = (token: string): string => `Bearer ${token}`;

const acquire = async (cacheMode: "on" | "off"): Promise<string | null> => {
  if (isNavigationLatched() || isTerminal()) return null;

  const client = await getClient();
  try {
    return bearer(await client.getTokenSilently({ cacheMode }));
  } catch (error) {
    if (isLoginRequiredError(error)) {
      await requestLogin();
      return null;
    }
    throw error;
  }
};

/**
 * The single seam every caller uses. The two modes deliberately differ in
 * scheme: the legacy backend expects the bare token with no scheme, which is
 * what `endpoints/config.ts` has always sent. Callers must not add a prefix of
 * their own.
 */
export const getAuthHeader = async (): Promise<string | null> => {
  if (!isAuth0Enabled()) return tokenManager.getToken();
  return acquire("on");
};

let inFlightRefresh: Promise<string | null> | null = null;

/**
 * For the 401 refresh-and-retry. Pass the bare token that just failed so a
 * sibling 401's rotation can be reused: if the cached token already differs
 * from the rejected one, another request refreshed it and we return it with no
 * new grant. Otherwise a single shared refresh is forced — spa-js does not
 * dedupe `cacheMode:'off'` grants (its in-lock cache re-read is skipped for
 * 'off'), so N concurrent 401s would trigger N sequential rotations without the
 * single-flight below. Called with no argument it forces a refresh directly.
 */
export const getFreshAuthHeader = async (
  rejectedToken?: string,
): Promise<string | null> => {
  if (!isAuth0Enabled()) return tokenManager.getToken();
  if (isNavigationLatched() || isTerminal()) return null;

  const client = await getClient();

  if (rejectedToken) {
    try {
      const cached = await client.getTokenSilently({ cacheMode: "on" });
      if (cached !== rejectedToken) return bearer(cached);
    } catch (error) {
      if (!isLoginRequiredError(error)) throw error;
    }
  }

  if (!inFlightRefresh) {
    inFlightRefresh = (async () => {
      try {
        return bearer(await client.getTokenSilently({ cacheMode: "off" }));
      } catch (error) {
        if (isLoginRequiredError(error)) {
          await requestLogin();
          return null;
        }
        throw error;
      } finally {
        inFlightRefresh = null;
      }
    })();
  }
  return inFlightRefresh;
};

export const isAuthenticated = async (): Promise<boolean> => {
  if (!isAuth0Enabled()) return tokenManager.hasToken();
  if (isTerminal()) return false;
  const client = await getClient();
  return client.isAuthenticated();
};

export const currentPhase = () => getAuthStatus().phase;
